import React from 'react';

import {
  SaveTriageWorkflowInput,
  TriageWorkflowTypeName,
  TriageWorkflowsWidgetDocument,
  useSaveTriageWorkflowsMutation,
  useTriageWorkflowsWidgetQuery
} from 'generated/graphql';

import * as types from './types';



export interface IData {
  formState: types.IFormState;
}

export function useQuery(storeId: string) {
  const tuple = useTriageWorkflowsWidgetQuery({
    variables: {
      storeId
    }
  });

  return React.useMemo(
    () => {
      function __getTriageWorkflowTypeValue(workflowTypeName: string) {
        return tuple.data?.store?.triageWorkflows.find(tw => tw.type.name === workflowTypeName);
      }

      const triageWorkflows = {
        receivingWorkflow: __getTriageWorkflowTypeValue('receiving'),
        triageWorkflow: __getTriageWorkflowTypeValue('triage'),
        replacementWorkflow: __getTriageWorkflowTypeValue('replacement'),
        repairWorkflow: __getTriageWorkflowTypeValue('restock'),
        returnWorkflow: __getTriageWorkflowTypeValue('return'),
        creditWorkflow: __getTriageWorkflowTypeValue('credit'),
        scrapWorkflow: __getTriageWorkflowTypeValue('scrap')
      };

      const data: IData = {
        formState: {
          receivingWorkflow: {
            id: triageWorkflows.receivingWorkflow?.id,
            enabled: triageWorkflows.receivingWorkflow?.isEnabled ? 'true' : 'false',
            name: triageWorkflows.receivingWorkflow?.name || '',
            binId: triageWorkflows.receivingWorkflow?.netSuiteBinInternalId || ''
          },
          triageWorkflow: {
            id: triageWorkflows.triageWorkflow?.id,
            enabled: triageWorkflows.triageWorkflow?.isEnabled ? 'true' : 'false',
            name: triageWorkflows.triageWorkflow?.name || '',
            binId: triageWorkflows.triageWorkflow?.netSuiteBinInternalId || ''
          },
          replacementWorkflow: {
            id: triageWorkflows.replacementWorkflow?.id,
            enabled: triageWorkflows.replacementWorkflow?.isEnabled ? 'true' : 'false',
            name: triageWorkflows.replacementWorkflow?.name || '',
            binId: triageWorkflows.replacementWorkflow?.netSuiteBinInternalId || ''
          },
          repairWorkflow: {
            id: triageWorkflows.repairWorkflow?.id,
            enabled: triageWorkflows.repairWorkflow?.isEnabled ? 'true' : 'false',
            name: triageWorkflows.repairWorkflow?.name || '',
            binId: triageWorkflows.repairWorkflow?.netSuiteBinInternalId || ''
          },
          returnWorkflow: {
            id: triageWorkflows.returnWorkflow?.id,
            enabled: triageWorkflows.returnWorkflow?.isEnabled ? 'true' : 'false',
            name: triageWorkflows.returnWorkflow?.name || '',
            binId: triageWorkflows.returnWorkflow?.netSuiteBinInternalId || ''
          },
          creditWorkflow: {
            id: triageWorkflows.creditWorkflow?.id,
            enabled: triageWorkflows.creditWorkflow?.isEnabled ? 'true' : 'false',
            name: triageWorkflows.creditWorkflow?.name || '',
            binId: triageWorkflows.creditWorkflow?.netSuiteBinInternalId || ''
          },
          scrapWorkflow: {
            id: triageWorkflows.scrapWorkflow?.id,
            enabled: triageWorkflows.scrapWorkflow?.isEnabled ? 'true' : 'false',
            name: triageWorkflows.scrapWorkflow?.name || '',
            binId: triageWorkflows.scrapWorkflow?.netSuiteBinInternalId || ''
          }
        }
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}


export function useSaveTriageWorkflows(storeId: string) {
  const [fnMutate, tuple] = useSaveTriageWorkflowsMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: TriageWorkflowsWidgetDocument,
        variables: {
          storeId: storeId
        }
      }
    ]
  });

  const fnWrappedMutate = React.useCallback(
    async (formState: types.IFormState) => {
      await fnMutate({
        variables: {
          input: {
            storeId,
            triageWorkflowsInput: [
              __formWorkflow2GqlInput(formState.triageWorkflow, 'triage'),
              __formWorkflow2GqlInput(formState.receivingWorkflow, 'receiving'),
              __formWorkflow2GqlInput(formState.replacementWorkflow, 'replacement'),
              __formWorkflow2GqlInput(formState.repairWorkflow, 'restock'),
              __formWorkflow2GqlInput(formState.returnWorkflow, 'return'),
              __formWorkflow2GqlInput(formState.creditWorkflow, 'credit'),
              __formWorkflow2GqlInput(formState.scrapWorkflow, 'scrap')
            ]
          }
        }
      });
    },
    [
      storeId,
      fnMutate
    ]
  );
  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}



function __formWorkflow2GqlInput(formState: types.ITriageWorkflow, triageWorkflowTypeName: TriageWorkflowTypeName): SaveTriageWorkflowInput {
  return {
    id: formState.id,
    isEnabled: formState.enabled === 'true',
    name: formState.name,
    netSuiteBinInternalId: formState.binId,
    triageWorkflowTypeName
  };
}
